/* ---------------------------------------------------------------------
   Sub-category page (sections/main-plp.liquid) and category page
   (sections/main-collection.liquid): the Shop-by rows filter the listing
   in place, the way the home-page quiz rebuilds a rail.

   The two pages draw their grids with different class names — .plp-grid
   on a sub-category, .clp-container > .clp-grid on a category — so the
   rebuilt listing is drawn in whichever the page uses, and the cards sit
   in it exactly as the server's own did.

   Rows on the page:
     Shop By Category   circles with data-vro-shopby-row="category"
     Shop By Occasion … bubbles with data-vro-shopby-collection
     Shop By Price      bubbles with data-vro-shopby-min / -max

   Tapping a bubble no longer leaves the page. One pick per row; tapping
   the picked bubble again clears that row. The picks combine:

     base      = the picked category's collection, or this page's own
     narrowed  = products of base that are also in every picked
                 Occasion / Concern / Goal collection, and whose price
                 sits in the picked band

   How it answers without downloading the shelf:
     1. /collections/<base>?view=vro-handles lists the base collection's
        products as [handle, price] (templates/collection.vro-handles.liquid),
        in the collection's own order;
     2. the same view of each picked Occasion / Concern / Goal collection
        gives the handles to intersect with;
     3. only the cards that will be shown are fetched, 24 at a time, from
        /products/<handle>?view=vro-card (templates/product.vro-card.liquid)
        — the theme's own feed-product-card, identical markup, so the
        delegated add-to-cart / wishlist handlers keep working.

   Any storefront filter already in the URL (filter.v.* / filter.p.*) is
   passed through to the base list, so the Filters sheet and the rows agree.

   The picks are written to ?shop=row:value,... so a reload or a shared
   link shows the same listing. Clearing everything puts the
   server-rendered grid back exactly as it was.

   If a fetch fails, the tap falls back to the bubble's own link, which is
   what these bubbles did before this file existed.
--------------------------------------------------------------------- */
(function () {
  'use strict';

  /* The View All tile at a product card's size. Sharing a row it already
     stretches to the row; alone in the last row it would shrink to its
     words, so it takes the height of the card before it, and again on
     resize. */
  function sizeLikeCard(tile) {
    var grid = tile.parentNode;
    if (!grid) return;
    var cards = grid.querySelectorAll('.feed-card');
    var card = cards.length ? cards[cards.length - 1] : null;
    if (!card) return;
    var h = card.getBoundingClientRect().height;
    if (h > 0) tile.style.minHeight = Math.round(h) + 'px';
  }

  var MAX_PAGES = 12;        /* pages of the handles view, 250 products each */
  var SHOW_STEP = 24;        /* cards fetched per press of the View All tile */

  /* The theme's View All glyph, same as the tile every rail closes with
     (assets/vro-plp.js, assets/quiz-results.js). */
  var VIEW_ALL_ICON =
    '<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
    'stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<path d="M9 3.5c3 0 5.5 1.8 6.5 4.3l4-1.3-1.8 3.9c1 .9 1.6 2 1.6 3.3 0 3.3-4.6 6-9.6 6-5.2 0-9.7-3-9.7-6.7C0 9.2 3.9 3.5 9 3.5Z"/>' +
    '<circle cx="6.5" cy="12.5" r="1.2" fill="currentColor" stroke="none"/></svg>';
  var CONCURRENCY = 6;       /* card requests at once */

  var ROOT_URL = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  var root, grid, status, pageHandle;
  var isCategory = false;    /* sections/main-collection.liquid (.clp) */
  var original = null;
  var state = {};            /* rowKey -> { el, handle?, min?, max?, label } */
  var listCache = {};
  var cardCache = {};
  /* handle -> the variant id its card is fetched for, from the base list
     the current listing was built from (see apply()). */
  var cardVariant = {};
  var token = 0;
  var lastHref = null;

  /* The in-page quiz (assets/vro-plp-quiz.js) narrows the same listing
     these rows narrow, so the two cannot each own the grid: one would
     draw over the other's answer. Instead the quiz hands this file a
     filter — handles in, the ones that suit the answer out — and every
     rebuild runs through it. Shop-by still decides WHICH products are on
     the shelf; the quiz only decides which of those are shown. */
  var narrow = null;

  /* ---------- helpers ---------- */

  function all(sel, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(sel));
  }

  function num(v) {
    if (v === null || v === undefined || v === '') return null;
    var n = parseFloat(v);
    return isNaN(n) ? null : n;
  }

  function parseHTML(html) {
    var t = document.createElement('template');
    t.innerHTML = html;
    return t.content;
  }

  function fetchText(url) {
    return fetch(url, { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.text();
    });
  }

  /* filter.* and sort_by parameters currently in the address bar, as a
     query suffix — the bar's Sort applies to a rebuilt listing too. */
  function filterSuffix() {
    var out = [];
    new URLSearchParams(window.location.search).forEach(function (v, k) {
      /* An empty price box's filter.v.price.gte= reads as a bound of 0. */
      if (v === '') return;
      if (k.indexOf('filter.') === 0 || k === 'sort_by') out.push(encodeURIComponent(k) + '=' + encodeURIComponent(v));
    });
    return out.length ? '&' + out.join('&') : '';
  }

  /* ---------- data ---------- */

  /* A collection's products as [{ h: handle, p: price in paise }], in its
     own order. withFilters: carry the page's filter.* parameters. */
  function listOf(handle, withFilters, only) {
    /* only: a storefront filter a bubble stands for ("Oily" on Grooming is
       filter.p.m.custom.skin_type=Oily), which the handles view applies
       like any other filter in its URL. */
    var suffix = (withFilters ? filterSuffix() : '') + (only ? '&' + only : '');
    var key = handle + suffix;
    if (listCache[key]) return listCache[key];

    var base = ROOT_URL + 'collections/' + encodeURIComponent(handle) + '?view=vro-handles' + suffix;

    function page(n) {
      return fetchText(base + '&page=' + n).then(function (text) {
        var data = JSON.parse(text);
        return data;
      });
    }

    var job = page(1).then(function (first) {
      var pages = Math.min(parseInt(first.pages, 10) || 1, MAX_PAGES);
      var rest = [];
      for (var p = 2; p <= pages; p++) rest.push(page(p));
      return Promise.all(rest).then(function (more) {
        var seen = {};
        var list = [];
        [first].concat(more).forEach(function (data) {
          (data.items || []).forEach(function (item) {
            if (!item || seen[item[0]]) return;
            seen[item[0]] = true;
            /* item[2]: the variant the card stands for — the cheapest
               in-stock one inside the price filter — so the card fetched
               for it shows that price, not a sold-out size's. */
            list.push({ h: item[0], p: num(item[1]), v: item[2] ? String(item[2]) : '', a: item[3] !== 0 });
          });
        });
        list.set = seen;
        return list;
      });
    }, function (err) {
      /* A collection that doesn't exist (404) is simply empty. */
      if (String(err && err.message).indexOf('404') !== -1) {
        var none = [];
        none.set = {};
        return none;
      }
      throw err;
    });

    job.catch(function () { delete listCache[key]; });
    listCache[key] = job;
    return job;
  }

  /* One product's listing card, or null if it can't be drawn. */
  function cardFor(handle) {
    var vid = cardVariant[handle] || '';
    var key = handle + '|' + vid;
    if (cardCache[key]) return cardCache[key];
    var url = ROOT_URL + 'products/' + encodeURIComponent(handle) + '?view=vro-card' + (vid ? '&variant=' + encodeURIComponent(vid) : '');
    /* Two dozen card requests leave at once, and the shop answers a few of
       them with 503 under that burst. A dropped card used to mean a gap in
       the grid — the shopper sees 21 products where the bar says 24 — so
       one retry, a moment later, when the first attempt fails. */
    var job = fetchText(url).catch(function () {
      return new Promise(function (go) { setTimeout(go, 400); }).then(function () {
        return fetchText(url);
      });
    }).then(function (html) {
      return parseHTML(html).querySelector('.feed-card');
    }).catch(function () { return null; });
    cardCache[key] = job;
    return job;
  }

  /* Cards for a slice of handles, in order, a few requests at a time. */
  function cardsFor(handles) {
    var out = new Array(handles.length);
    var next = 0;
    function worker() {
      if (next >= handles.length) return Promise.resolve();
      var i = next++;
      return cardFor(handles[i]).then(function (card) {
        out[i] = card;
        return worker();
      });
    }
    var workers = [];
    for (var w = 0; w < Math.min(CONCURRENCY, handles.length); w++) workers.push(worker());
    return Promise.all(workers).then(function () {
      return out.filter(Boolean);
    });
  }

  /* ---------- state <-> bubbles ---------- */

  function describe(el) {
    return {
      el: el,
      row: el.getAttribute('data-vro-shopby-row'),
      handle: el.getAttribute('data-vro-shopby-collection') || null,
      filter: el.getAttribute('data-vro-shopby-filter') || null,
      min: num(el.getAttribute('data-vro-shopby-min')),
      max: num(el.getAttribute('data-vro-shopby-max')),
      label: el.getAttribute('data-vro-shopby-label') || el.textContent.trim()
    };
  }

  function isPrice(pick) {
    return !pick.handle && (pick.min !== null || pick.max !== null);
  }

  function valueOf(pick) {
    if (isPrice(pick)) return (pick.min === null ? '' : pick.min) + '-' + (pick.max === null ? '' : pick.max);
    /* A filter bubble shares its collection with every other bubble in its
       row (all five skin types are Grooming & Care), so the filter's own
       value is what tells them apart — and what goes in ?shop=. */
    if (pick.filter) {
      var v = pick.filter.split('=').pop();
      try { v = decodeURIComponent(v.replace(/\+/g, ' ')); } catch (e) { /* keep */ }
      return pick.handle + '.' + v.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    }
    return pick.handle;
  }

  function same(a, b) {
    return a && b && a.row === b.row && valueOf(a) === valueOf(b);
  }

  function anyPicked() {
    for (var k in state) if (state[k]) return true;
    return false;
  }

  function syncBubbles() {
    all('[data-vro-shopby-row]', root).forEach(function (el) {
      var row = el.getAttribute('data-vro-shopby-row');
      var pick = state[row];
      var on;
      if (pick) {
        on = same(pick, describe(el));
      } else {
        on = el.hasAttribute('data-vro-shopby-initial');
      }
      el.classList.toggle('is-current', on);
      el.classList.toggle('is-picked', !!(pick && on));
      el.setAttribute('aria-pressed', pick && on ? 'true' : 'false');
    });
  }

  function writeUrl() {
    var params = new URLSearchParams(window.location.search);
    var parts = [];
    Object.keys(state).forEach(function (row) {
      if (state[row]) parts.push(row + ':' + valueOf(state[row]));
    });
    if (parts.length) params.set('shop', parts.join(','));
    else params.delete('shop');
    params.delete('page');
    var qs = params.toString();
    var url = window.location.pathname + (qs ? '?' + qs.replace(/%3A/gi, ':').replace(/%2C/gi, ',') : '') + window.location.hash;
    try { window.history.replaceState(window.history.state, '', url); } catch (e) { /* ignore */ }
  }

  function readUrl() {
    var raw = new URLSearchParams(window.location.search).get('shop');
    if (!raw) return;
    var bubbles = all('[data-vro-shopby-row]', root).map(describe);
    raw.split(',').forEach(function (part) {
      var at = part.indexOf(':');
      if (at < 1) return;
      var row = part.slice(0, at);
      var value = part.slice(at + 1);
      for (var i = 0; i < bubbles.length; i++) {
        if (bubbles[i].row === row && valueOf(bubbles[i]) === value) {
          state[row] = bubbles[i];
          return;
        }
      }
    });
  }

  /* ---------- rendering ---------- */

  function setStatus(mode, count) {
    if (!status) return;
    status.innerHTML = '';
    if (mode === 'off') { status.hidden = true; return; }
    /* A rebuild with no bubble picked is the quiz's doing, and the quiz
       says so in its own bar. An empty "· 12 products" strip above the
       grid would be a second, wordless answer to the same question. */
    if (!anyPicked()) { status.hidden = true; return; }
    status.hidden = false;

    var labels = [];
    Object.keys(state).forEach(function (row) {
      if (state[row]) labels.push(state[row].label);
    });

    var text = document.createElement('span');
    text.className = 'plp-shopby-status-text';

    var title = document.createElement('span');
    title.className = 'plp-shopby-status-title';
    title.textContent = labels.join(' · ');
    text.appendChild(title);

    var detail = document.createElement('span');
    detail.className = 'plp-shopby-status-detail';
    if (mode === 'loading') detail.textContent = 'Finding matching products…';
    else detail.textContent = count === 1 ? '1 product' : count + ' products';
    text.appendChild(detail);

    status.appendChild(text);

    var clear = document.createElement('button');
    clear.type = 'button';
    clear.className = 'plp-shopby-clear';
    clear.textContent = 'Clear';
    clear.addEventListener('click', clearAll);
    status.appendChild(clear);

    status.classList.toggle('is-loading', mode === 'loading');
  }

  /* The old products stay on screen, dimmed, until the new ones are
     ready; then they trade places in one step while the grid eases to its
     new height and the first cards settle in. Clearing the grid first and
     filling it later was the blink: an empty page for as long as the
     cards took to arrive. */
  function morph(fill) {
    var m = window.VroMotion;
    var put = function () {
      fill();
      var n = 0;
      all('.feed-card', grid).forEach(function (card) {
        if (n > 8) return;
        card.style.animationDelay = (n++ * 40) + 'ms';
        card.classList.add('vro-settle-in');
      });
    };
    if (m && m.morphHeight && !m.reduced()) m.morphHeight(grid, put, { duration: 460 });
    else put();
    requestAnimationFrame(function () {
      grid.classList.remove('is-loading');
      grid.classList.remove('vro-filter-loading');
    });
  }

  function restore() {
    if (original !== null && grid.innerHTML !== original) morph(function () { grid.innerHTML = original; });
    else grid.classList.remove('is-loading');
    setStatus('off');
  }

  /* A category page's grid sits inside the page's .clp-container, which
     is what gives it its width and side padding; a sub-category's does
     not. */
  function framed(el) {
    if (!isCategory) return el;
    var box = document.createElement('div');
    box.className = 'clp-container';
    box.appendChild(el);
    return box;
  }

  /* On a category page a goal chip can also be narrowing the grid
     (assets/vro-subfilter.js, data-vro-facet-rails). The cards just drawn
     are new to it, so it is asked to apply its answer to them too —
     otherwise "Casual" under an answered "Energy" chip would show every
     casual product and the chip would be quietly ignored. */
  function answeredAgain() {
    if (!isCategory || !window.VroSubfilter || !window.VroSubfilter.refresh) return;
    if (!document.querySelector('[data-vro-facet].vro-facet-answered')) return;
    try { window.VroSubfilter.refresh(); } catch (e) { /* its own business */ }
  }

  function render(handles, mine) {
    if (!handles.length) {
      var empty = document.createElement('div');
      empty.className = 'plp-empty';
      var t = document.createElement('p');
      t.className = 'plp-empty-title';
      t.textContent = 'No products match this selection yet';
      var n = document.createElement('p');
      n.className = 'plp-empty-note';
      n.textContent = 'Try removing one of the choices above.';
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'plp-empty-back';
      b.textContent = 'Clear selection';
      b.addEventListener('click', clearAll);
      empty.appendChild(t);
      empty.appendChild(n);
      empty.appendChild(b);
      morph(function () {
        grid.innerHTML = '';
        grid.appendChild(framed(empty));
      });
      setStatus('done', 0);
      return Promise.resolve();
    }

    var list = document.createElement('div');
    list.className = isCategory ? 'clp-grid' : 'plp-grid';
    var placed = false;

    var shown = 0;
    /* The way to the rest is a card at the end of the grid rather than a
       pill under it: the theme's View All tile, at a product card's size,
       in the next cell along — so the row ends on something the eye reads
       as part of the grid. It still loads the next batch in place. */
    var more = document.createElement('button');
    more.type = 'button';
    more.className = 'feed-view-all plp-shopby-more';
    window.addEventListener('resize', function () { sizeLikeCard(more); });
    var moreIcon = document.createElement('span');
    moreIcon.className = 'feed-view-all-icon';
    moreIcon.innerHTML = VIEW_ALL_ICON;
    more.appendChild(moreIcon);
    var moreText = document.createElement('span');
    moreText.className = 'feed-view-all-text';
    var moreLabel = document.createElement('span');
    moreLabel.className = 'feed-view-all-label';
    moreLabel.textContent = 'View All';
    moreText.appendChild(moreLabel);
    var moreCount = document.createElement('span');
    moreCount.className = 'feed-view-all-category';
    moreText.appendChild(moreCount);
    more.appendChild(moreText);

    /* The tile also loads the next batch by itself once it nears the
       viewport; the click stays as the fallback where there is no
       IntersectionObserver. */
    var watcher = null;
    function watch() {
      if (!('IntersectionObserver' in window)) return;
      if (!watcher) {
        watcher = new IntersectionObserver(function (entries) {
          for (var i = 0; i < entries.length; i++) {
            if (!entries[i].isIntersecting) continue;
            if (mine !== token || !more.parentNode) { watcher.disconnect(); return; }
            if (!more.disabled) step();
          }
        }, { rootMargin: '600px 0px' });
      }
      /* unobserve first so a tile that is still in view after a batch
         lands reports again and pulls the next one. */
      watcher.unobserve(more);
      watcher.observe(more);
    }

    function step() {
      if (more.disabled) return Promise.resolve();
      var slice = handles.slice(shown, shown + SHOW_STEP);
      shown += slice.length;
      more.disabled = true;
      moreCount.textContent = 'Loading…';
      return cardsFor(slice).then(function (cards) {
        if (mine !== token) return;
        var added = [];
        for (var i = 0; i < cards.length; i++) added.push(list.appendChild(document.importNode(cards[i], true)));
        sizeLikeCard(more);
        more.disabled = false;
        var ready = (!placed && window.VroMotion && window.VroMotion.preload)
          ? window.VroMotion.preload(added, { limit: 8, timeout: 1500 }) : Promise.resolve();
        return ready.then(function () { if (mine === token) land(); });
      });
    }

    function land() {
      if (shown >= handles.length) {
        if (more.parentNode) more.parentNode.removeChild(more);
        if (watcher) watcher.disconnect();
      } else {
        moreCount.textContent = (handles.length - shown) + ' more';
        /* appendChild on a child it already has moves it, which is what
           keeps the tile the last cell after each batch lands. */
        list.appendChild(more);
        sizeLikeCard(more);
      }
      if (!placed) {
        placed = true;
        morph(function () {
          grid.innerHTML = '';
          grid.appendChild(framed(list));
        });
        answeredAgain();
      }
      document.dispatchEvent(new CustomEvent('vro:cards-inserted', { detail: { root: list } }));
      if (shown < handles.length) watch();
    }

    more.addEventListener('click', step);
    return step().then(function () {
      if (mine === token) setStatus('done', handles.length);
    });
  }

  /* ---------- filter counts follow the picked category ----------
     The Filters dropdowns are drawn by the server for this page's own
     collection (Face Care), but a Shop-by category pick (Face Wash)
     narrows the grid to another collection, so "Oily (6)" sat over a grid
     of 3. With a category picked, the counts are fetched for that
     collection (sections/vro-facet-counts.liquid, same filters applied)
     and written over the page's; clearing the pick puts the page's back. */
  var facetOriginal = null;
  var facetToken = 0;

  function facetInputs() {
    return all('input[type="checkbox"][name^="filter."]');
  }
  function facetLabel(input) {
    var label = input.closest('label');
    if (!label) return null;
    var spans = label.querySelectorAll('span');
    for (var i = spans.length - 1; i >= 0; i--) if (/\(\d+\)\s*$/.test(spans[i].textContent)) return spans[i];
    return null;
  }
  /* Applying a filter while a category is picked swaps the bar and the
     sheet for the server's (assets/vro-filter-motion.js, applyOwned), so
     the inputs remembered here are no longer the ones on screen. Writing
     the category's counts to those left the page's own counts showing —
     "Oily (6)" from Face Care over a Face Wash grid of 3. So entries whose
     input has left the page are dropped, and any input not yet known is
     read fresh: a newly swapped-in input still carries the server's count.
     Inputs that stayed keep their first reading, which may already have
     been written over. */
  function facetSnapshot() {
    var kept = (facetOriginal || []).filter(function (o) { return o.input.isConnected; });
    var known = kept.map(function (o) { return o.input; });
    facetInputs().forEach(function (input) {
      if (known.indexOf(input) !== -1) return;
      var span = facetLabel(input);
      var label = input.closest('label');
      kept.push({
        input: input, span: span, text: span ? span.textContent : null,
        disabled: input.disabled, off: label ? label.classList.contains('is-disabled') : false
      });
    });
    facetOriginal = kept;
  }
  function facetRestore() {
    if (window.VroFacetCount) window.VroFacetCount.reset();
    if (!facetOriginal) return;
    facetOriginal.forEach(function (o) {
      if (o.span) o.span.textContent = o.text;
      o.input.disabled = o.disabled;
      var label = o.input.closest('label');
      if (label) label.classList.toggle('is-disabled', o.off);
    });
  }
  /* ---------- the bar, for a picked category ----------

     A category circle lists another collection — often a sibling of this
     page's rather than a part of it — but the Filters bar was still this
     page's: its values and price range. A value only the picked category
     has (a size, a brand) was never offered. So the bar is swapped for
     the same bar drawn for the picked collection (this section, rendered
     by Shopify for it with the applied filters), whose values and counts
     are its own; and put back when the category is cleared.

     The inside of the form is swapped, never the element: custom.js and
     assets/vro-filter-motion.js hold on to it. A disabled hidden input
     marks a swapped bar, so a bar redrawn from the server (a filter
     applied) is recognised as this page's again. */
  var barOriginal = null;
  function sectionIdOf() {
    var host = root && root.closest ? root.closest('[id^="shopify-section-"]') : null;
    return host ? host.id.replace('shopify-section-', '') : '';
  }
  function barMarker(form) {
    var m = form && form.querySelector('input[data-vro-bar-for]');
    return m ? m.getAttribute('data-vro-bar-for') : pageHandle;
  }
  function samePath(href) {
    try {
      var u = new URL(href, window.location.href);
      u.pathname = window.location.pathname;
      return u.pathname + u.search + u.hash;
    } catch (e) { return href; }
  }
  function syncBar(base, mine) {
    var form = document.getElementById('FacetFiltersForm');
    var sheet = document.querySelector('[data-plp-sheet] .plp-sheet-panel');
    if (!form) return Promise.resolve();
    var showing = barMarker(form);
    if (base === showing) return Promise.resolve();
    if (base === pageHandle) {
      if (barOriginal) {
        form.innerHTML = barOriginal.form;
        if (sheet && barOriginal.sheet !== null) sheet.innerHTML = barOriginal.sheet;
      }
      return Promise.resolve();
    }
    var sid = sectionIdOf();
    if (!sid) return Promise.resolve();
    var q = filterSuffix().replace(/^&/, '');
    return fetchText(ROOT_URL + 'collections/' + encodeURIComponent(base) + '?' + (q ? q + '&' : '') + 'section_id=' + encodeURIComponent(sid))
      .then(function (html) {
        if (mine !== token) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var next = doc.getElementById('FacetFiltersForm');
        if (!next) return;
        if (barMarker(form) === pageHandle) {
          barOriginal = { form: form.innerHTML, sheet: sheet ? sheet.innerHTML : null };
        }
        form.innerHTML = next.innerHTML;
        var nextSheet = doc.querySelector('[data-plp-sheet] .plp-sheet-panel');
        if (sheet && nextSheet) sheet.innerHTML = nextSheet.innerHTML;
        var marker = document.createElement('input');
        marker.type = 'hidden';
        marker.disabled = true;
        marker.setAttribute('data-vro-bar-for', base);
        form.appendChild(marker);
        /* Its links were drawn for the picked collection's own page. */
        [form, sheet].forEach(function (box) {
          if (!box) return;
          box.querySelectorAll('a[href]').forEach(function (a) { a.setAttribute('href', samePath(a.getAttribute('href'))); });
        });
      })
      .catch(function () { /* this page's bar stays */ });
  }

  function facetCounts(base) {
    var mine = ++facetToken;
    /* A picked price band narrows the grid too, so the counts take it as
       Shopify's own price filter — "Oily (6)" under "Under ₹999" counts
       the oily products under ₹999. Only when the URL carries no price
       filter of its own, which would otherwise be sent twice. */
    /* With a price filter in the URL as well, the grid shows what is in
       both ranges, so the counts take the overlap: the higher floor and
       the lower ceiling, sent once in place of the URL's own pair. */
    var band = '';
    var lo = null, hi = null, picked = false;
    Object.keys(state).forEach(function (row) {
      var pick = state[row];
      if (!pick || !isPrice(pick)) return;
      picked = true;
      if (pick.min !== null) lo = pick.min;
      if (pick.max !== null) hi = pick.max;
    });
    var urlPrice = new URLSearchParams(window.location.search);
    if (picked) {
      var ulo = parseFloat(urlPrice.get('filter.v.price.gte'));
      var uhi = parseFloat(urlPrice.get('filter.v.price.lte'));
      if (!isNaN(ulo)) lo = lo === null ? ulo : Math.max(lo, ulo);
      if (!isNaN(uhi)) hi = hi === null ? uhi : Math.min(hi, uhi);
      if (lo !== null) band += '&filter.v.price.gte=' + encodeURIComponent(lo);
      if (hi !== null) band += '&filter.v.price.lte=' + encodeURIComponent(hi);
    }
    if ((!base || base === pageHandle) && !band) { facetRestore(); return; }
    base = base || pageHandle;
    facetSnapshot();
    var suffix = filterSuffix();
    if (picked) suffix = suffix.replace(/&filter\.v\.price\.(gte|lte)=[^&]*/g, '');
    var q = suffix.replace(/^&/, '') + band;
    q = q.replace(/^&/, '');
    fetchText(ROOT_URL + 'collections/' + encodeURIComponent(base) + '?' + (q ? q + '&' : '') + 'section_id=vro-facet-counts')
      .then(function (html) {
        if (mine !== facetToken) return;
        var doc = parseHTML(html);
        var node = doc.querySelector('[data-vro-facet-counts]');
        if (!node) return;
        var counts = JSON.parse(node.textContent);
        facetOriginal.forEach(function (o) {
          var n = counts[o.input.name + '|' + o.input.value] || 0;
          if (o.span) o.span.textContent = o.text.replace(/\(\d+\)\s*$/, '(' + n + ')');
          var off = n === 0 && !o.input.checked;
          o.input.disabled = off;
          var label = o.input.closest('label');
          if (label) label.classList.toggle('is-disabled', off);
        });
      })
      .catch(function () { /* keep whatever is showing */ });
  }

  /* ---------- apply ---------- */

  function apply() {
    var mine = ++token;
    syncBubbles();
    writeUrl();

    /* A bubble that narrows by another collection or a filter of its own
       (Occasion, Concern, Goal, "Oily"), or the quiz's answer: Shopify's
       counts can't see those, so they are worked out from the products
       the grid keeps (assets/vro-facet-count.js) once the lists are in.
       A category or a price band alone, Shopify counts exactly. */
    var deep = !!window.VroFacetCount && (!!narrow || Object.keys(state).some(function (row) {
      var pick = state[row];
      return pick && row !== 'category' && !isPrice(pick);
    }));
    var barBase = state.category ? state.category.handle : pageHandle;
    facetToken++;
    var barReady = syncBar(barBase, mine).then(function () {
      if (mine === token && !deep) facetCounts(barBase);
    });

    if (!anyPicked() && !narrow) { restore(); return; }

    grid.classList.add('is-loading');
    setStatus('loading');

    var base = state.category ? state.category.handle : pageHandle;
    var price = null;
    var needs = [listOf(base, true)];
    Object.keys(state).forEach(function (row) {
      var pick = state[row];
      if (!pick || row === 'category') return;
      if (isPrice(pick)) price = pick;
      else needs.push(listOf(pick.handle, false, pick.filter));
    });

    Promise.all(needs).then(function (res) {
      if (mine !== token) return;
      var items = res[0];
      var sets = res.slice(1).map(function (l) { return l.set; });

      /* After the quiz has judged this shelf, so its answer's strength is
         the one the counts use. */
      function paintDeep() {
        if (!deep || mine !== token) return;
        var allowed = narrow && narrow.allowed ? narrow.allowed(base) : null;
        Promise.all([allowed, barReady]).then(function (got) {
          if (mine !== token) return;
          var allow = got[0];
          facetSnapshot();
          window.VroFacetCount.paint({ base: base, sets: sets, allow: allow || null, band: price });
        });
      }

      var handles = [];
      /* Lists are cached, so the variants come from the list this listing
         is built from, not whichever list happened to load last. */
      cardVariant = {};
      items.forEach(function (item) { if (item.v) cardVariant[item.h] = item.v; });
      items.forEach(function (item) {
        for (var i = 0; i < sets.length; i++) if (!sets[i][item.h]) return;
        if (price) {
          if (item.p === null) return;
          var v = item.p / 100;
          if (price.min !== null && v < price.min) return;
          if (price.max !== null && v > price.max) return;
        }
        handles.push(item.h);
      });

      /* In stock first, sold out after, each keeping the collection's
         order — across the whole shelf, not page by page. */
      var soldOut = {};
      items.forEach(function (item) { if (item.a === false) soldOut[item.h] = true; });
      handles = handles.filter(function (h) { return !soldOut[h]; })
        .concat(handles.filter(function (h) { return soldOut[h]; }));

      if (!narrow) { paintDeep(); return render(handles, mine); }

      /* The quiz answers from the same shelf the rows left standing, so
         picking T-Shirts and then answering "Cotton" asks the question of
         cotton t-shirts, not of cotton anything. */
      return Promise.resolve(narrow(handles, base)).then(function (kept) {
        if (mine !== token) return;
        paintDeep();
        /* The quiz ranks by fit, which can lift a sold-out product over
           one in stock; stock still decides first, fit within it. */
        var ranked = kept || handles;
        ranked = ranked.filter(function (h) { return !soldOut[h]; })
          .concat(ranked.filter(function (h) { return soldOut[h]; }));
        return render(ranked, mine);
      });
    }).catch(function () {
      if (mine !== token) return;
      grid.classList.remove('is-loading');
      if (lastHref) window.location.href = lastHref;
      else restore();
    });
  }

  function clearAll() {
    state = {};
    apply();
  }

  function onTap(e) {
    var el = e.target.closest('[data-vro-shopby-row]');
    if (!el || !root.contains(el)) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return; /* new tab keeps the link */
    e.preventDefault();

    var pick = describe(el);
    lastHref = el.getAttribute('href');

    if (pick.row === 'category' && pick.handle === pageHandle) {
      /* This page's own circle: the base is already this collection. */
      state.category = null;
    } else if (same(state[pick.row], pick)) {
      state[pick.row] = null;
    } else {
      state[pick.row] = pick;
    }
    apply();
  }

  /* The first tap was the slow one: a big shelf (Fashion & Lifestyle is
     eight pages of handles) had to be listed before anything could be
     narrowed. So the shelf is listed quietly once the rows come into
     view, and a bubble's own collection as soon as a pointer lands on it
     — by the time the tap arrives, most of the answer is already here.
     Nothing is drawn by this; it only fills the same caches apply() reads. */
  function warm() {
    var idle = window.requestIdleCallback || function (fn) { return setTimeout(fn, 200); };
    var shelfWarm = false;
    function shelf() {
      if (shelfWarm) return;
      shelfWarm = true;
      idle(function () { listOf(pageHandle, true).catch(function () {}); });
    }
    var first = root.querySelector('[data-vro-shopby-row]');
    if (first && 'IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        if (entries.some(function (en) { return en.isIntersecting; })) { io.disconnect(); shelf(); }
      }, { rootMargin: '200px 0px' });
      io.observe(first);
    } else if (first) {
      shelf();
    }
    function lean(e) {
      var el = e.target.closest && e.target.closest('[data-vro-shopby-row]');
      if (!el || !root.contains(el)) return;
      shelf();
      var h = el.getAttribute('data-vro-shopby-collection');
      if (!h) return;
      var withFilters = el.getAttribute('data-vro-shopby-row') === 'category';
      listOf(h, withFilters, withFilters ? null : el.getAttribute('data-vro-shopby-filter')).catch(function () {});
    }
    root.addEventListener('pointerover', lean, { passive: true });
    root.addEventListener('pointerdown', lean, { passive: true });
  }


  /* ---------- in stock first, across every page ----------

     The server orders each page in two passes, in stock then sold out,
     but only within the 40 it was sent: page 1 ended on its sold-out
     products and page 2 opened on in-stock ones again. So, on a listing
     with more than one page and nothing picked, the shelf's own
     [handle, price, variant, in stock] list (the handles view, same
     filters and sort as the page) is read in the background, the products
     that belong on this page are worked out — every in-stock product
     before any sold-out one, each in the shelf's order — and the cards
     that differ are swapped into the same slots. Pages, page links,
     banners and layout stay exactly as the server drew them.

     The list is read a page (250) at a time and only as far as needed:
     page 1 usually needs one request. Anything picked, a quiz narrowing
     the grid, or a failed request leaves the server's page as it was. */
  var STOCK_MAX_PAGES = 30;
  var stockRun = 0;

  function handleOfCard(card) {
    var own = card.getAttribute('data-vro-handle');
    if (own) return own;
    var a = card.querySelector('a[href*="/products/"]');
    var m = a && /\/products\/([^/?#]+)/.exec(a.getAttribute('href') || '');
    return m ? decodeURIComponent(m[1]) : '';
  }

  function stockOrder() {
    if (!grid || !pageHandle) return;
    var nav = root.querySelector('[data-vro-page]');
    if (!nav) return;                       /* one page: the server's two passes already cover it */
    if (new URLSearchParams(window.location.search).get('shop')) return;
    var page = parseInt(nav.getAttribute('data-vro-page'), 10) || 1;
    var per = parseInt(nav.getAttribute('data-vro-per-page'), 10) || 0;
    if (!per) return;
    var run = ++stockRun;
    var startToken = token;

    var base = ROOT_URL + 'collections/' + encodeURIComponent(pageHandle) + '?view=vro-handles' + filterSuffix();
    var inStock = [];
    var soldOut = [];
    var variantOf = {};
    var need = page * per;

    function read(n) {
      return fetchText(base + '&page=' + n).then(function (text) {
        var data = JSON.parse(text);
        (data.items || []).forEach(function (item) {
          if (!item) return;
          if (item[2]) variantOf[item[0]] = String(item[2]);
          (item[3] === 0 ? soldOut : inStock).push(item[0]);
        });
        var pages = Math.min(parseInt(data.pages, 10) || 1, STOCK_MAX_PAGES);
        /* Enough in-stock products to fill this page: the sold-out ones
           further down cannot reach it. */
        if (inStock.length >= need || n >= pages) return;
        return read(n + 1);
      });
    }

    read(1).then(function () {
      if (run !== stockRun || token !== startToken || anyPicked() || narrow) return;
      var order = inStock.length >= need ? inStock : inStock.concat(soldOut);
      var want = order.slice((page - 1) * per, page * per);

      /* The listing's own cards only: a category page's grid wrap also
         holds the engagement card and quiz rails between its two grids,
         whose cards are not slots of this page. */
      var slots = all('.plp-grid > .feed-card, .clp-grid > .feed-card', grid);
      if (!slots.length || !want.length) return;
      var have = slots.map(handleOfCard);
      if (have.indexOf('') > -1) return;
      var count = Math.min(slots.length, want.length);
      var same = true;
      for (var i = 0; i < count; i++) if (have[i] !== want[i]) { same = false; break; }
      if (same) return;

      var byHandle = {};
      slots.forEach(function (el, i) { byHandle[have[i]] = el; });
      var missing = [];
      for (var j = 0; j < count; j++) if (!byHandle[want[j]]) missing.push(want[j]);
      missing.forEach(function (h) { if (variantOf[h]) cardVariant[h] = variantOf[h]; });

      return cardsFor(missing).then(function (cards) {
        if (run !== stockRun || token !== startToken || anyPicked() || narrow) return;
        var fetched = {};
        cards.forEach(function (c) { fetched[handleOfCard(c)] = c; });
        /* Every wanted card must be in hand, or the page would come out
           short; otherwise the server's page stays. */
        for (var k = 0; k < count; k++) {
          if (!byHandle[want[k]] && !fetched[want[k]]) return;
        }
        var marks = slots.slice(0, count).map(function (el) {
          var mark = document.createComment('');
          el.parentNode.insertBefore(mark, el);
          return mark;
        });
        slots.slice(0, count).forEach(function (el) { el.parentNode.removeChild(el); });
        /* assets/vro-grid-balance.js keeps the first half's cards (by
           node) to move across the banner; it is told the new ones, in the
           new order, or it would put the old cards back. */
        var firstGrid = grid.querySelector('.plp-grid, .clp-grid');
        var halfLen = firstGrid && firstGrid.__vroBalanceOrig ? firstGrid.__vroBalanceOrig.length : 0;
        var added = [];
        var placed = [];
        marks.forEach(function (mark, k) {
          var node = byHandle[want[k]] || document.importNode(fetched[want[k]], true);
          if (!byHandle[want[k]]) added.push(node);
          mark.parentNode.replaceChild(node, mark);
          placed.push(node);
        });
        if (halfLen) firstGrid.__vroBalanceOrig = placed.slice(0, halfLen);
        document.dispatchEvent(new CustomEvent('vro:grid:reordered'));
        original = grid.innerHTML;
        if (added.length) {
          document.dispatchEvent(new CustomEvent('vro:cards-inserted', { detail: { root: grid } }));
        }
        answeredAgain();
      });
    }).catch(function () { /* the server's page stands */ });
  }

  function init() {
    root = document.querySelector('[data-vro-shopby]');
    if (!root) return;
    grid = root.querySelector('[data-vro-shopby-grid]');
    status = root.querySelector('[data-vro-shopby-status]');
    pageHandle = root.getAttribute('data-plp-collection');
    if (!grid || !pageHandle) return;
    isCategory = root.classList.contains('clp');
    stockOrder();
    /* A page number swaps the next page's grid in without a reload
       (assets/vro-page-swap.js): that page is put in stock order too, and
       becomes what Clear goes back to. */
    document.addEventListener('vro:grid:swapped', function () {
      if (anyPicked() || narrow) return;
      original = grid.innerHTML;
      stockOrder();
    });
    /* A page with neither rows nor a quiz has nothing to rebuild for. The
       quiz counts, because on a listing whose only rows are elsewhere it
       is the one thing that narrows the grid. */
    if (!root.querySelector('[data-vro-shopby-row]') &&
        !root.querySelector('.plp-quiz .quiz-teaser-card')) return;

    original = grid.innerHTML;

    all('[data-vro-shopby-row]', root).forEach(function (el) {
      if (el.classList.contains('is-current')) el.setAttribute('data-vro-shopby-initial', '');
      el.setAttribute('role', 'button');
    });

    root.addEventListener('click', onTap);
    warm();

    readUrl();

    /* What the quiz needs from here, and nothing more: the shelf it is
       answering from, a way to register its filter, and a way to ask for
       the listing to be drawn again. */
    window.VroPlpShopBy = {
      base: function () { return state.category ? state.category.handle : pageHandle; },
      picked: anyPicked,
      setNarrow: function (fn) { narrow = fn || null; apply(); },
      refresh: apply,
      /* assets/vro-filter-motion.js, after a filter: the server's grid for
         the filtered shelf becomes what Clear and Retake go back to. */
      setOriginal: function (html) { original = html; stockOrder(); },
      grid: function () { return grid; }
    };
    document.dispatchEvent(new CustomEvent('vro:shopby:ready'));

    if (anyPicked()) apply();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
